import { useState } from "react";

import {
  SuccessModalContent,
  useStepperDispatch,
  Typography,
  InfoPanel,
  Modal,
} from "@king-ict/eupisi-ui/components";
import { KindergartenRoleEnum } from "@king-ict/eupisi-ui/models";
import {
  hrDate,
  getInstitutionDisplayNameAndTown,
} from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router";
import { useHistory, useParams } from "react-router-dom";

import {
  useCreateRequestMutation,
  useUpdateRequestMutation,
} from "../../../api/requestApi";
import { RootState, useTypedSelector } from "../../../app/rootReducer";
import { useAppDispatch } from "../../../app/store";

import { useCurrentUser } from "../../../hooks/authorization/useCurrentUser";
import EnrollmentTypeEnum from "../../../models/EnrollmentType/EnrollmentTypeEnum";
import ParentNumberEnum from "../../../models/Person/ParentNumberEnum";
import CreateUpdateRequest from "../../../models/Request/CreateUpdateRequest";
import InitialConversationQuestionDataItem from "../../../models/Request/InitialConversationQuestionDataItem";
import RequestStatusEnum from "../../../models/Request/RequestStatusEnum";
import {
  adminRequestsUrl,
  adminSubsequentRequestsUrl,
  adminPreschoolRequestsUrl,
  dashboard,
  draftPreschoolRequestRoute,
  editPreschoolRequestRoute,
  newPreschoolRequestRoute,
} from "../../../routes/routes";
import { clearRequestData } from "../../../slices/requestSlice";
import ApplicantParentDetails from "../../RequestInfo/RequestDetails/ApplicantParentDetails";
import ChildDetails from "../../RequestInfo/RequestDetails/ChildDetails";
import CriterionDetails from "../../RequestInfo/RequestDetails/CriterionDetails";
import InitialConversationDetails from "../../RequestInfo/RequestDetails/InitialConversationDetails";
import InstitutionProgramDetails from "../../RequestInfo/RequestDetails/InstitutionProgramDetails";
import OtherParentDetails from "../../RequestInfo/RequestDetails/OtherParentDetails";
import styles from "./RequestReviewStep.module.scss";

interface Props {
  moveToSpecificStep: (step: number, substep?: number) => void;
}

export default function RequestReviewStep({ moveToSpecificStep }: Props) {
  const request = useSelector((state: RootState) => state.request.request);
  const user = useCurrentUser();
  const userRoleId = user.roleId;
  const currentImportantDate = useSelector(
    (state: RootState) => state.pedagogicalYear.currentImportantDate
  );

  const [requestCode, setRequestCode] = useState("");
  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);

  const newPreschoolRequestMatch = useRouteMatch(newPreschoolRequestRoute());
  const draftPreschoolRequestMatch = useRouteMatch(
    draftPreschoolRequestRoute()
  );
  const editPreschoolRequestMatch = useRouteMatch(editPreschoolRequestRoute());

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openRequestSuccessModal, setOpenRequestSuccessModal] = useState(false);
  const [
    createRequest,
    { isLoading: isCreatingRequest },
  ] = useCreateRequestMutation();
  const [
    updateRequest,
    { isLoading: isUpdatingRequest },
  ] = useUpdateRequestMutation();
  const isSubmittingRequest = isCreatingRequest || isUpdatingRequest;

  const { requestId, guid } = useParams<{
    requestId: string;
    guid: string;
  }>();
  const dispatch = useAppDispatch();

  const history = useHistory();

  const handleToggleConfirmModal = () => {
    setOpenConfirmModal((openConfirmModal) => !openConfirmModal);
  };

  const handleToggleRequestSuccessModal = () => {
    setOpenRequestSuccessModal(
      (openRequestSuccessModal) => !openRequestSuccessModal
    );
  };

  const handleRequest = async () => {
    if (isSubmittingRequest) {
      return;
    }

    const questionsData: InitialConversationQuestionDataItem[] = [];
    request.initialConversationQuestionGroups.forEach((item) => {
      questionsData.push(...item.questionsData);
    });

    const newRequest: CreateUpdateRequest = {
      ...request,
      initialConversationItems: [...questionsData],
      pedagogicalYearInstitutionId:
        request.selectedInstitution.pedagogicalYearInstitutionId,
      requestDraftId: guid,
    };

    try {
      const code = requestId
        ? await updateRequest({ request: newRequest, id: requestId }).unwrap()
        : await createRequest(newRequest).unwrap();

      setOpenConfirmModal(false);
      setRequestCode(code);
      handleToggleRequestSuccessModal();
    } catch {
      // Global API interceptor shows request errors.
    }
  };

  const handleBackToDashboard = () => {
    handleToggleRequestSuccessModal();
    dispatch(clearRequestData());

    if (user.institutionId) {
      window.location.href =
        newPreschoolRequestMatch != null ||
        editPreschoolRequestMatch != null ||
        draftPreschoolRequestMatch != null
          ? adminPreschoolRequestsUrl()
          : pedagogicalYear.currentImportantDate.enrollmentTypeId ===
            EnrollmentTypeEnum.SubsequentEnrollment
          ? adminSubsequentRequestsUrl()
          : adminRequestsUrl();
    } else {
      history.push(dashboard());
    }
  };

  const handleLastStep = () => {
    handleToggleConfirmModal();
  };

  useStepperDispatch({
    type: "next",
    payload: () => {
      return handleLastStep();
    },
  });

  const { t } = useTranslation();

  const emptyValue = "-";

  return (
    <>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          <Typography variant="smallHeading">
            {t("requestReviewStep.header")}
          </Typography>
        </Col>
      </Row>

      <ApplicantParentDetails
        emptyValue={emptyValue}
        applicantParent={request.applicantParent}
        moveToSpecificStep={moveToSpecificStep}
      />
      <hr />

      {request.parentNumberId === ParentNumberEnum.TwoParents &&
        request.otherParent && (
          <>
            <OtherParentDetails
              emptyValue={emptyValue}
              otherParent={request.otherParent}
              moveToSpecificStep={moveToSpecificStep}
            />
            <hr />
          </>
        )}

      <ChildDetails
        emptyValue={emptyValue}
        child={request.child}
        moveToSpecificStep={moveToSpecificStep}
      />
      <hr />

      <InstitutionProgramDetails
        institutionPrograms={request.institutionPrograms}
        moveToSpecificStep={moveToSpecificStep}
      />
      <hr />

      <CriterionDetails
        applicantOib={request.applicantParent?.oib}
        otherParentOib={request.otherParent?.oib}
        childOib={request.child.oib}
        criteria={request.criterionData.criteria}
        moveToSpecificStep={moveToSpecificStep}
      />
      <hr />
      {request.selectedInstitution.usingQuestionnaire && (
        <>
          <InitialConversationDetails
            emptyValue={emptyValue}
            questionGroups={request.initialConversationQuestionGroups}
            moveToSpecificStep={moveToSpecificStep}
          />
          <hr />
        </>
      )}

      <Row className={styles.subHeader}>
        <Col md={12}>
          <InfoPanel
            message={t("request.physicalExaminationInfoMessage")}
            color="success"
            fullWidth
          />
        </Col>
      </Row>

      <hr />
      <Modal
        type={"confirm"}
        title={t("request.sendRequestConfirmModalTitle")}
        open={openConfirmModal}
        confirmText={t("request.submitButton")}
        cancelText={t("common.cancel")}
        onClose={handleToggleConfirmModal}
        onConfirm={handleRequest}
      >
        <div>
          {request.status.id === RequestStatusEnum.Unlocked
            ? t("request.unlockedRequestConfirmModalMessage", {
                institutionName: request.selectedInstitution.name,
              })
            : t("request.sendRequestConfirmModalMessage", {
                endDate: hrDate(currentImportantDate.dateTimeTo),
              })}
        </div>
      </Modal>

      <Modal
        type={"message"}
        open={openRequestSuccessModal}
        confirmText={t("common.close")}
        onConfirm={handleBackToDashboard}
        onClose={() => handleToggleRequestSuccessModal()}
      >
        <SuccessModalContent
          message={
            request.status.id === RequestStatusEnum.Unlocked
              ? t("requestDetails.sendUnlockedRequestSuccessfullMessage", {
                  institutionName: request.selectedInstitution.name,
                })
              : userRoleId === KindergartenRoleEnum.Parent
              ? t("requestDetails.sendRequestSuccesfullMessage", {
                  endDate: hrDate(currentImportantDate.dateTimeTo),
                  institution: getInstitutionDisplayNameAndTown(
                    request.selectedInstitution.name,
                    request.selectedInstitution.town
                  ),
                })
              : t("requestDetails.sendRequestSuccessfullAdminMessage", {
                  endDate: hrDate(currentImportantDate.dateTimeTo),
                })
          }
        >
          {request.status.id !== RequestStatusEnum.Unlocked && (
            <>
              <Row>
                <Typography variant="small">
                  {t("requestReviewStep.requestUid", {
                    requestUid: request.uId,
                  })}
                </Typography>
              </Row>
              <Row>
                <Typography variant="small">
                  {t("requestReviewStep.requestCode", { code: requestCode })}
                </Typography>
              </Row>
            </>
          )}
        </SuccessModalContent>
      </Modal>
    </>
  );
}
